import type { FormDefinition, FormField } from "../types";

/**
 * Personio application payload built from a HubSpot recruiting form submission.
 * Contact fields map to Personio standard fields; everything else is folded
 * into `message` (Personio's free-text "Description" on the candidate).
 */
export interface PersonioApplicationPayload {
  job_position_id: number;
  first_name: string;
  last_name: string;
  email: string;
  message: string;
}

/** Personio "Initiativbewerbung (m/w/d)" — fallback when no position is configured. */
export const PERSONIO_INITIATIVBEWERBUNG_ID = 1603207;

// Recruiting forms use inconsistent field names (vorname/firstname, mail_bewerber/email…),
// so match on both the field name and its label.
// ponytail: heuristic name/label match. If a form uses an unrecognized name for a
// contact field, it gracefully lands in the description instead (no data loss) —
// extend these regexes if a new form names them differently.
const FIRST_NAME = /vorname|first[\s_-]?name/i;
const LAST_NAME = /nachname|last[\s_-]?name|surname|familienname/i;
const EMAIL = /e[\s_-]?mail|^mail/i;

const matches = (re: RegExp, ...candidates: (string | undefined)[]) =>
  candidates.some((c) => c && re.test(c));

/** Strip the trailing " *" the form mapper appends to required-field labels. */
const cleanLabel = (label: string) => label.replace(/\s*\*\s*$/, "").trim();

/** For selects, show the chosen option's label instead of its raw value. */
const displayValue = (field: FormField, raw: unknown): string => {
  const value = String(raw);
  const option = field.options?.find((o) => o.value === value);
  return option ? option.label : value;
};

/**
 * Maps a HubSpot form definition + submitted values to a Personio application.
 * - Vorname / Nachname / E-Mail → first_name / last_name / email
 * - all other applicant fields → "Label: Wert" lines in `message`
 *
 * Only `formDefinition.fields` (applicant-entered) are considered, so HubSpot
 * tracking fields (utm_*, kampagnen_*) and consent checkboxes never leak into Personio.
 */
export function buildPersonioApplication(
  formDefinition: FormDefinition,
  values: Record<string, unknown>,
  jobPositionId: number
): PersonioApplicationPayload {
  let first_name = "";
  let last_name = "";
  let email = "";
  const lines: string[] = [];

  for (const field of formDefinition.fields) {
    const raw = values[field.name];
    if (raw === undefined || raw === null || raw === "") continue;

    if (matches(FIRST_NAME, field.name, field.label)) {
      first_name = String(raw);
    } else if (matches(LAST_NAME, field.name, field.label)) {
      last_name = String(raw);
    } else if (matches(EMAIL, field.name, field.label)) {
      email = String(raw);
    } else {
      lines.push(`${cleanLabel(field.label)}: ${displayValue(field, raw)}`);
    }
  }

  return {
    job_position_id: jobPositionId,
    first_name,
    last_name,
    email,
    message: lines.join("\n"),
  };
}
